import React, { useEffect, useMemo, useRef, useState } from 'react'
import { addCommas, bindClick, bindFormChange, cropString, fromUnix, getNow } from '@arema/components/Util';
import { CatalogEvent, EventDate, SetLoading } from '@arema/components/Classes'; 
import { AssetFile, AssetType, PdvCorte, PdvDepositSource, PDVDepositType, PDVDepositTypes } from '../../AdminClasses';
import { DeleteAssetModal, SearchCorteModal, SearchDateModal, SearchEventModal, UploadAssetButton, UserLogModal } from '../../components'
import { Button, Field, Groupper, Header, Icon, Input, Message, SegmentControl, Table, useFrontier, Modal, Dropdown, Label } from 'react-frontier2'
import { useUser } from '../../AdminHooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PDVDeposit } from '@arema/types/internal';
import { Money } from '@arema/components';
import Validator from '@arema/components/Validator';
import API from '../../API';
import UserAccess from '../../UserAccess';
import classNames from 'classnames';

interface CreateDepositModalProps {
	depositId?: number,
	pdvId: number,
	open: boolean,
	onClose: ()=>void,
	onSave: (d: PDVDeposit, source: PdvDepositSource, dt: PDVDepositType)=>void,
}

interface ValidDates{
	date_id: number,
	event_id: number,
	event_name: string,
	date: number,
}

enum CreateDepositType{
	DEPOSIT = 1,
	OTHER = 2,
}

export default function CreateDepositModal(props: CreateDepositModalProps){
	var { t } = useTranslation();
	var { hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [deposit, setDeposit] = useState<PDVDeposit>(null);
	var [cortes, setCortes] = useState<PDVDeposit["cortes"]>([]);
	var [openCorteModal, setOpenCorteModal] = useState<boolean>(false);
	var [assets, setAssets] = useState<AssetFile[]>(null);
	var [deletingAsset, setDeletingAsset] = useState<number>(null);
	var [sources, setSources] = useState<PdvDepositSource[]>([]);
	var [depositTypes, setDepositTypes] = useState<PDVDepositType[]>();
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [selectedType, setSelectedType] = useState<CreateDepositType>(CreateDepositType.DEPOSIT);
	var [validDates, setValidDates] = useState<{ cortes: number[], dates: ValidDates[] }>(null);
	var [depositDates, setDepositDates] = useState<(ValidDates & { amount: number })[]>(null);
	var [datesError, setDatesError] = useState<string>(null);
	var [datesModal, setDatesModal] = useState<boolean>(false);
	var [searchDateModal, setSearchDateModal] = useState<boolean>(false);
	var [logShown, setLogShown] = useState<boolean>(false);

	useEffect(()=>{
		getSources().then(src=>{
			if(!src) return props.onClose();
		});
	}, [props.depositId]);

	useEffect(()=>{
		if(!props.open){
			setDeposit(null);
			setCortes([]);
			setAssets([]);
			setDepositDates([]);
			setSelectedType(CreateDepositType.DEPOSIT);
		}else{
			if(!props.depositId){
				if(!deposit){
					setDeposit({
						deposit_id: null,
						date_deposit: null,
						deposit_type: null,
						source_id: null,
						description: '',
						deleted: false,
						payout_id: null,
						cortes: null,
						payout: null,
					});
					setDepositDates([]);
					setCortes([]);
					setAssets([]);
				}
			}else{
				getDeposit(props.depositId);
			}
		}
	}, [props.open, props.depositId]);

	useEffect(()=>{
		if(depositTypes) return;
		if(selectedType===CreateDepositType.OTHER){
			getDepositTypes();
		}
	}, [depositTypes, selectedType]);

	useEffect(()=>{
		if(selectedType!==CreateDepositType.OTHER || !cortes || cortes.length==0){
			if(!validDates) return setValidDates({ cortes: [], dates: [] });
			return;
		}
		if(!!validDates){
			var cortes_ids = cortes.map(a=>a.corte_id).sort((a,b)=>a-b);
			var curr_cortes = validDates.cortes.sort((a,b)=>a-b);
			if(cortes_ids.length===validDates.cortes.length){
				var equal = true;
				for(var l=0; l<cortes_ids.length; l++){
					if(cortes_ids[l]!==curr_cortes[l]){
						equal = false;
						break;
					}
				}
				if(equal) return;
			}
		}
		getValidDates();
	}, [selectedType, cortes, validDates]);

	var deleting_asset = useMemo(()=>{
		if(!deletingAsset) return null;
		return assets.find(a=>a.asset_id===deletingAsset);
	}, [deletingAsset]);

	var getValidDates = ()=>{
		setValidDates(null);
		setDatesError(null);
		API.getCorteDates(props.pdvId, cortes.map(a=>a.corte_id)).then(res=>{
			if(res.error) return setDatesError(res.message);
			setValidDates({
				cortes: cortes.map(a=>a.corte_id),
				dates: res.data,
			});
		}).catch(err=>{
			setDatesError(t('common.unexpected_code', { code: 'LCL-CRDPMDL-CRTDT-1' }));
		});
	}

	var getDeposit = (deposit_id: number)=>{
		API.getPdvDeposit(deposit_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setDeposit(res.data);
			if(res.data.deposit_type!==CreateDepositType.DEPOSIT){
				setSelectedType(CreateDepositType.OTHER);
			}
			setCortes(res.data.cortes);
			setAssets(res.data.assets);
		}).catch(()=>{
			Toast.error('Hubo un error inesperado cargando el deposito (LCL-3)');
			props.onClose();
		})
	}

	var getSources = async ()=>{
		try{
			var res = await API.getPdvDepositSource();
			if(res.error){
				Toast.error(res.message);
				return false;
			}
			setSources(res.data);
			return true;
		}catch(error){
			Toast.error('Error inesperado al cargar cuentas (LCL-2)')
			props.onClose();
			return false;
		}
	}

	var getDepositTypes = ()=>{
		API.getPdvDepositTypes().then(res=>{
			if(res.error) return Toast.error(res.message);
			setDepositTypes(res.data);
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-GTDEPTYP-1' }))
		})
	}

	var onSelectCorte = (corte: PdvCorte)=>{
		if(cortes.findIndex(c=>c.corte_id === corte.corte_id)>-1){
			return Toast.error('Este corte ya esta seleccionado');
		}
		setCortes([
			...cortes,
			{
				amount_id: null,
				corte_id: corte.corte_id,
				date_start: corte.date_start,
				cash: corte.total_cash,
				deposit: corte.deposit,
				amount: Math.max(corte.total_cash-corte.deposit, 0),
				date_created: null,
			}
		])
		setOpenCorteModal(false);
	}

	var onCorteAmountChange = (corte_id: number)=>{
		return (value: string)=>{
			var c = [...cortes];
			var ix = c.findIndex(a=>a.corte_id===corte_id);
			if(ix!=-1) c[ix].amount = parseFloat(value);
			setCortes(c);
		}
	}

	var onDateAmountChange = (date_id: number)=>{
		return (value: string)=>{
			var d = [...depositDates];
			var ix = d.findIndex(a=>a.date_id===date_id);
			if(ix!=-1) d[ix].amount = parseFloat(value);
			setDepositDates(d);
		}
	}

	var onDateSelected = (date: ValidDates)=>{
		return ()=>{
			var ix = depositDates ? depositDates.findIndex(a=>a.date_id==date.date_id) : -1;
			if(ix!==-1) return setDatesModal(false);

			if(depositDates){
				for(var i of depositDates){
					if(i.event_id!==date.event_id){
						Toast.error('No se pueden mezclar diferentes eventos')
						return setDatesModal(false);
					}
				}
			}

			setDepositDates(d=>([
				...(d || []),
				{ ...date, amount: 0, }
			]));
			setDatesModal(false);
		}
	}

	var onSearchDateSelected = (e: CatalogEvent, d: EventDate)=>{
		setDepositDates(dt=>([
			...(dt || []),
			{
				date_id: d.date_id,
				date: d.date,
				event_id: e.event_id,
				event_name: e.event_name,
				amount: 0,
			}
		]));
		setDatesModal(false);
		setSearchDateModal(false);
		return true;
	}

	var deleteDate = (date_id: number)=>{
		return ()=>{
			if(!depositDates) return;
			setDepositDates(d=>d.filter(a=>a.date_id!==date_id));
		}
	}

	var deleteAmount = (ix: number)=>{
		return ()=>{
			var c = [...cortes];
			c.splice(ix, 1);
			setCortes(c);
		}
	}

	var onAssetUpload = (asset: AssetFile)=>{
		setAssets([
			...(assets || []),
			asset
		]);
	}

	var onAssetDeleted = (asset_id: number)=>{
		return setAssets(assets.filter(a=>a.asset_id!==asset_id));
	}

	var onSubmitDeposit = (setLoading: SetLoading)=>{
		if(deposit.deposit_id) return;
		var is_normal = selectedType===CreateDepositType.DEPOSIT;

		var cortes_amount = 0;
		for(var i of cortes){
			cortes_amount += i.amount;
		}
		var { valid, prompts } = Validator({ ...deposit, cortes, dates: depositDates }, {
			date_deposit: [{ rule: 'empty', label: 'Fecha deposito' }],
			description: [{ rule: 'length', params: [3, 512], label: 'Información' }],
			source_id: is_normal ? [{ rule: 'empty', label: 'Cuenta contable' }] : [],
			deposit_type: is_normal ? [] : [{
				rule: 'empty', prompt: 'Favor de seleccionar el tipo de deposito'
			}],
			cortes: [{
				rule: 'minLength', params: [1], prompt: 'Favor de seleccionar uno o mas cortes'
			}, {
				rule: (v: PDVDeposit["cortes"])=>{
					if(v.length==0) return false;
					for(var i of v){
						if(i.amount<=0) return false;
					}
					return true;
				},
				prompt: 'El valor de los cortes debe de ser mayor a 0'
			}],
			dates: is_normal ? [] : [{
				rule: (v: typeof depositDates)=>{
					if(!v || v.length===0) return false;
					var total_amount = 0;
					for(var i of v){
						if(i.amount<=0) return false;
						total_amount += i.amount;
					}
					if(total_amount!==cortes_amount) return false;
					return true;
				},
				prompt: 'La cantidad de los calendarios debe de ser igual a la cantidad depositada en los cortes'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createPdvDeposit(props.pdvId, {
			date_deposit: deposit.date_deposit,
			source_id: is_normal ? deposit.source_id : null,
			deposit_type: is_normal ? PDVDepositTypes.DEPOSIT : deposit.deposit_type,
			description: deposit.description,
			cortes: cortes.map(a=>({
				corte_id: a.corte_id,
				amount: a.amount,
			})),
			dates: is_normal ? null : (depositDates ? depositDates.map(a=>({
				date_id: a.date_id,
				amount: a.amount,
			})) : []),
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var source = deposit.source_id ? sources.find(s=>s.source_id == deposit.source_id) : null;
			var dt = deposit.deposit_type ? depositTypes.find(a=>a.type_id===deposit.deposit_type) : null;
			setDeposit({
				...deposit,
				deposit_id: res.data.deposit_id,
			})
			props.onSave({
				...deposit,
				deposit_id: res.data.deposit_id,
				payout_id: res.data.payout_id,
				cortes: cortes,
			}, source, dt);
			return Toast.success('Se ha creado el deposito del PDV')
		}).catch((err)=>{
			return Toast.error('Error inesperado al guardar el deposito (LCL-1)')
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onFormChange = bindFormChange(deposit, setDeposit);
	var deposit_id = deposit?.deposit_id || props.depositId || null;
	var disabled = !!deposit_id;

	var {
		total_deposit_amount,
		total_dates_amount,
	} = useMemo(()=>{
		var total_deposit_amount = 0;
		var total_dates_amount = 0;
		for(var i of cortes){
			total_deposit_amount += parseFloat(i.amount as any) || 0;
		}
		if(depositDates){
			for(var d of depositDates){
				total_dates_amount += d.amount;
			}
		}
		return {
			total_deposit_amount,
			total_dates_amount,
		};
	}, [cortes, depositDates]);
	
	return (<>
		<Modal open={props.open} onClose={props.onClose} size='small'>
			<Modal.Header>{`${deposit_id ? `Deposito #${deposit_id}` : 'Nuevo Deposito'}`}</Modal.Header>
			<Modal.Content>
				{!deposit ? <Header loading text='Cargando deposito' /> : (<>
					<Input label="Fecha deposito" value={disabled ? fromUnix(deposit.date_deposit).toFormat('dd/MMM/yy') : null} calendar={disabled ? null : { 
						date: deposit.date_deposit ? fromUnix(deposit.date_deposit).toJSDate() : null,
						initialDate: getNow().toJSDate(), 
						mode: 'date',
						onSelected: d=>onFormChange('date_deposit')(Math.floor(d.getTime()/1000))
					}} readonly={disabled} />
					<Input textarea label='Información' value={deposit?.description || ''} onChange={onFormChange('description')} readonly={disabled} />
					<Field label='Tipo de deposito'>
						<SegmentControl
							style={{ width: '100%' }}
							segments={[
								{ value: CreateDepositType.DEPOSIT, text: 'Deposito' },
								{ value: CreateDepositType.OTHER, text: 'Con liquidación' },
							]}
							selected={[selectedType]}
							onClick={deposit.deposit_id ? ()=>{} : setSelectedType}
						/>
					</Field>
					{selectedType===CreateDepositType.DEPOSIT ? (
						<Field label="Cuenta contable">
							<Dropdown
								disabled={disabled}
								value={deposit.source_id}
								placeholder="Seleccionar cuenta"
								onValueChange={onFormChange('source_id')}
								items={sources.map(s=>({
									text: s.source_name, 
									value: s.source_id,
									meta: `ID: ${s.source_id}`,
								}))}
							/>
						</Field>
					) : <>
						{!deposit.deposit_id && (
							<Message type='info' style={{ marginBottom: 10 }} centered>
								{t('create_deposit.payout_deposit_message')}
							</Message>
						)}
						<Field label='Tipo de deposito'>
							<Dropdown
								disabled={disabled}
								value={deposit.deposit_type}
								placeholder="Tipo de deposito"
								onValueChange={onFormChange('deposit_type')}
								loading={!depositTypes}
								items={depositTypes ? depositTypes.filter(a=>a.type_id>1).map(s=>({
									text: t(`pdv_deposit_type.type_${s.type_id}`), 
									value: s.type_id,
									meta: `ID: ${s.type_id}`,
								})) : []}
							/>
						</Field>
					</>}
					<Groupper fitted title={'Cortes'} titleSize="small" titleRight={!deposit.deposit_id && (
						<Button text="Agregar" onClick={bindClick(setOpenCorteModal, true)} size="tiny" iconName="plus-circle" color="black" />
					)}>
						{!cortes || cortes.length === 0 ? (
							<Header size='small' text={'Agrega cortes'} subtext={'Agrega los cortes de este deposito'} style={{ padding: 20, margin: 0 }} />
						) : (
							<Table striped>
								<Table.Head data={deposit.deposit_id ? (
									[['ID', 'Fecha', 'Efectivo', 'Depositado']]
								) : (
									[['ID', 'Fecha', 'Efectivo', 'Restante', 'Depositado', '']]
								)} />
								<Table.Body>
									{cortes.map((c, i)=>(
										<Table.Row collapsingIndexes={deposit.deposit_id ? [0, 2, 3] : [0, 1, 2, 3, 5]} key={`test-${i}`} data={[
											c.corte_id,
											fromUnix(c.date_start).toFormat('dd/MMM/yy HH:mm'),
											addCommas(c.cash),
											...(deposit.deposit_id ? [] : [
												addCommas(c.cash-c.deposit),
											]),
											(c.amount_id || deposit?.deposit_id) ? `$${addCommas(c.amount)}` : (
												<Input
													style={{ margin: 0 }}
													placeholder="Cantidad depositada"
													value={c.amount.toString()}
													onChange={onCorteAmountChange(c.corte_id)}
													inputType='number'
												/>
											),
											...(deposit.deposit_id ? [] : [
												<Button iconName="remove" size="tiny" style={{ minWidth: 20 }} onClick={deleteAmount(i)} />
											])
										]} />
									))}
									<Table.Row>
										<Table.Cell colSpan={deposit.deposit_id ? 2 : 3} />
										<Table.Cell style={{ fontWeight: 'bold', color: 'gray' }}>{t('common.total')}</Table.Cell>
										<Table.Cell style={{ fontWeight: 'bold' }}>${addCommas(total_deposit_amount)}</Table.Cell>
										{!deposit.deposit_id && (
											<Table.Cell />
										)}
									</Table.Row>
								</Table.Body>
							</Table>
						)}
					</Groupper>
					{selectedType===CreateDepositType.OTHER && !deposit.deposit_id && (
						<Groupper fitted title={t('common.event_date')} style={{ marginTop: 10 }} titleRight={(
							<Button disabled={cortes.length==0} text={t('common.add')} iconName='circle-plus' color='black' onClick={bindClick(setDatesModal, true)} />
						)}>
							{cortes.length==0 ? (
								<Header size='small' text={t('create_deposit.no_cortes')} subtext={t('create_deposit.no_cortes_text')} style={{ margin: 0, padding: 20 }} />
							) : <>
								<Table style={{ borderRadius: 0 }}>
									{!depositDates || depositDates.length==0 ? (
										<Table.Cell row empty colSpan={100}>{t('create_deposit.select_dates')}</Table.Cell>
									) : depositDates.map(a=>(
										<Table.Row collapsingIndexes={[0, 2, 4]} data={[
											a.date_id,
											a.event_name,
											fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
											<Input
												style={{ margin: 0 }}
												placeholder="Cantidad depositada"
												value={a.amount.toString()}
												onChange={onDateAmountChange(a.date_id)}
												inputType='number'
											/>,
											...(deposit.deposit_id ? [] : [
												<Button iconName="remove" size="tiny" style={{ minWidth: 20 }} onClick={deleteDate(a.date_id)} />
											])
										]} />
									))}
								</Table>
								<Table fitted striped className='last' style={{ borderBottomRightRadius: 12, borderBottomLeftRadius: 12 }}>
									<Table.Row>
										<Table.Cell style={{ textAlign: 'right', fontWeight: 'bold' }}>{t('create_deposit.total_dates')}</Table.Cell>
										<Table.Cell collapsing>${addCommas(total_dates_amount)}</Table.Cell>
									</Table.Row>
									<Table.Row>
										<Table.Cell style={{ textAlign: 'right', fontWeight: 'bold' }}>{t('create_deposit.total_deposit')}</Table.Cell>
										<Table.Cell collapsing>${addCommas(total_deposit_amount)}</Table.Cell>
									</Table.Row>
									<Table.Row>
										<Table.Cell style={{ textAlign: 'right', fontWeight: 'bold' }}>{t('common.remaining')}</Table.Cell>
										<Table.Cell collapsing style={{ fontWeight: 'bold', color: total_deposit_amount-total_dates_amount<0 ? 'brown' : 'black' }}>${addCommas(total_deposit_amount-total_dates_amount)}</Table.Cell>
									</Table.Row>
								</Table>
							</>}
						</Groupper>
					)}
					{!!deposit && !!deposit.payout && (
						<Table style={{ marginTop: 15 }}>
							<Table.Head title={t('common.payout')}>
								<Table.Cell header centered row colSpan={100}>
									{t('create_deposit.payout_created')}
								</Table.Cell>
							</Table.Head>
							<Table.Body>
								<Table.Row compact selectable as={Link} to={`/payouts/${deposit.payout.payout_id}`} target={'_blank'} collapsingIndexes={[0, 2, 3]} data={[
									deposit.payout.payout_id,
									<Label color={deposit.payout.cancelled ? 'red' : deposit.payout.completed ? 'green' : 'blue'} value={deposit.payout.cancelled ? t('common.cancelled') : deposit.payout.completed ? t('common.completed') : t('common.pending')} />,
									fromUnix(deposit.payout.date_created).toFormat('dd/MMM/yy HH:mm'),
									<Money value={deposit.payout.amount} />
								]} />
							</Table.Body>
						</Table>
					)}
					{hasAccess(UserAccess.CONTABILIDAD.UPLOAD_EVIDENCE_PDV_DEPOSIT) ? (
						deposit.deposit_id ? (
							<Field label={t('common.evidence')} style={{ marginTop: 15 }}>
								<div className="ar images">
									{!!assets && assets.map((a, i)=>(
										<div
											className={classNames("image", {
												icon: a.type_id===AssetType.PDF
											})}
											key={`evidence-${a.asset_id}`}
										>
											{a.type_id===AssetType.IMAGE ? (
												<Link to={a.download_url} target='_blank'>
													<img src={a.download_url} />
												</Link>
											) : <Link to={a.download_url} target='_blank'>
												<Icon name='file-pdf' />
												<div className="text">{cropString(a.asset_name, 24, true)}</div>
											</Link>}
											{hasAccess(UserAccess.CONTABILIDAD.DELETE_EVIDENCE_PDV_DEPOSIT) && (
												<div className="delete" onClick={bindClick(setDeletingAsset, a.asset_id)}>
													<Icon name='remove' />
												</div>
											)}
										</div>
									))}
									<UploadAssetButton
										data={{ deposits: [deposit.deposit_id] }}
										typeId={[AssetType.IMAGE, AssetType.PDF]}
										onUpload={onAssetUpload}
										render={(loading, onClick)=>(
											<div className="image add" onClick={!loading ? onClick : null}>
												<Icon name={loading ? 'circle-notch' : 'plus'} style={{ lineHeight: '42px' }} loading={loading} />
											</div>
										)}
									/>
								</div>
							</Field>
						) : (
							<Message centered type='info' style={{ marginTop: 15 }}>
								{t('create_deposit.upload_message')}
							</Message>
						)
					) : (
						<Message centered style={{ marginTop: 15 }}>
							{t('create_deposit.cant_upload')}
						</Message>
					)}
					{deposit?.deposit_id && (
						<Button text='Ver usuarios' basic style={{ display: 'block', margin: 'auto', maxWidth: 200 }} onClick={bindClick(setLogShown, true)} />
					)}
				</>)}
				<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={props.onClose} />
				{!deposit?.deposit_id && (
					<Button text={t('common.save')} color='black' onClick={onSubmitDeposit} />
				)}
			</Modal.Actions>
		</Modal>
		<SearchCorteModal
			cashOnly
			pdvId={props.pdvId}
			open={openCorteModal}
			onClose={bindClick(setOpenCorteModal)}
			onCorteSelected={onSelectCorte}
		/>
		{!!deposit && !!deposit.deposit_id && (
			<DeleteAssetModal
				open={!!deleting_asset}
				asset={deleting_asset}
				data={{
					deposits: [deposit.deposit_id]
				}}
				onClose={bindClick(setDeletingAsset, null)}
				onDeleted={onAssetDeleted}
			/>
		)}
		<SearchEventModal date
			open={datesModal && searchDateModal}
			onClose={()=>{
				setDatesModal(false);
				setSearchDateModal(false);
			}}
			onDateSelected={onSearchDateSelected}
		/>
		<Modal open={datesModal && !searchDateModal} onClose={bindClick(setDatesModal, false)} size='tiny'>
			<Modal.Header>
				<div style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
					{t('create_deposit.select_date')}
					<Button size='tiny' color='black' iconName='search' text={'Buscar'} style={{ lineHeight: '14px', minWidth: 100 }} onClick={bindClick(setSearchDateModal, true)} />
				</div>
			</Modal.Header>
			<Modal.Content>
				{datesError ? (
					<Header text={t('common.error')} subtext={datesError} actions={<Button text={t('common.retry')} onClick={getValidDates} style={{ lineHeight: '16px', fontSize: 14 }} />} />
				) : validDates===null ? (
					<Header loading text={t('common.loading')} />
				) : validDates ? (
					<Table fitted className='last' style={{ marginTop: -20 }}>
						<Table.Head data={[[
							'ID',
							t('common.event'),
							t('common.date')
						]]} />
						<Table.Body>
							{validDates.dates.length==0 ? (
								<Table.Cell row empty colSpan={100}>
									{t('common.empty')}
								</Table.Cell>
							) : validDates.dates.map(a=>(
								<Table.Row
									selectable
									collapsingIndexes={[0, 2]}
									onClick={onDateSelected(a)}
									key={`DT-${a.date_id}`}
									data={[
										a.date_id,
										a.event_name,
										fromUnix(a.date).toFormat('dd/MMM/yy HH:mm')
									]}
								/>
							))}
						</Table.Body>
					</Table>
				) : null}
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={bindClick(setDatesModal, false)} />
			</Modal.Actions>
		</Modal>
		<UserLogModal open={logShown} onClose={bindClick(setLogShown)} user={deposit} />
	</>)
}
